import { Controller } from "@hotwired/stimulus"

// One row of the calculation drawer (Lohn, Stoffe, or a free row). Picking a Lohngruppe
// or an Artikel fills the row from the picked <option>'s data-* payload, so the user sees
// the KL / EK they are about to save instead of a blank field the server fills in later.
//
// It mirrors Costing::ComponentResolver: a Lohngruppe without a rate valid today leaves
// KL alone, an Artikel overwrites EK/Einheit/Verschnitt, and the Bezeichnung is only
// filled while it is empty. The server then saves what the row shows (keep_entered), so
// a value corrected after the prefill is kept.
//
// A value this controller wrote itself is not the user's, though: picking again replaces
// the previous pick's Bezeichnung, and a Lohngruppe without a rate clears the previous
// group's KL — otherwise keep_entered would save the old group's rate or name under the
// new pick.
//
// The row element is the controller element and is never moved — only its fields are
// written to.
export default class extends Controller {
  static targets = [
    "picker",
    "description",
    "quantity",
    "hourlyRate",
    "unitCost",
    "unit",
    "wastePercent",
  ]
  static values = { autoOpen: Boolean }

  connect() {
    this.prefilled = new Map()
    this.lastValue = this.hasPickerTarget ? this.pickerTarget.value : ""
    if (this.autoOpenValue) this.frame = requestAnimationFrame(() => this.openFirstField())
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
  }

  // SlimSelect fires `change` on the native select for every option-list rebuild as
  // well (each server search of the Artikel picker), so only a real change of value
  // counts as a pick.
  pick() {
    const value = this.pickerTarget.value
    if (value === this.lastValue) return

    this.lastValue = value
    const option = this.pickerTarget.selectedOptions[0]
    if (!value || !option) return

    const data = option.dataset
    if (this.hasHourlyRateTarget) {
      if (data.calculationWage) {
        this.write(this.hourlyRateTarget, data.calculationWage)
      } else if (this.prefilledByPick(this.hourlyRateTarget)) {
        this.write(this.hourlyRateTarget, "")
      }
    }
    // Every Artikel option carries the full payload; `name` is the marker.
    if (data.name !== undefined && this.hasUnitCostTarget) {
      this.write(this.unitCostTarget, data.unitCost ?? "")
      if (this.hasUnitTarget) this.write(this.unitTarget, data.unit ?? "")
      if (this.hasWastePercentTarget) this.write(this.wastePercentTarget, data.wastePercent ?? "0")
    }
    if (this.hasDescriptionTarget && data.name) {
      const description = this.descriptionTarget
      if (description.value.trim() === "" || this.prefilledByPick(description)) {
        this.write(description, data.name)
      }
    }

    // The quantity is the one value no master record can supply — hand the cursor
    // straight to it. Deferred: SlimSelect refocuses its own control while closing.
    if (this.hasQuantityTarget && this.quantityTarget.value === "") {
      this.frame = requestAnimationFrame(() => this.quantityTarget.focus())
    }
  }

  // A row just added with "+ Zeile hinzufügen": open its picker, or focus the first
  // field of a free row. Deferred one frame so the select controller has built its
  // SlimSelect and the click that added the row has finished — SlimSelect closes on
  // any document click outside it.
  openFirstField() {
    if (this.hasPickerTarget) {
      const select = this.application.getControllerForElementAndIdentifier(
        this.pickerTarget,
        "select",
      )
      if (select?.slimselect) {
        select.slimselect.open()
        return
      }
    }
    this.element.querySelector("input:not([type=hidden]), select")?.focus()
  }

  // Still showing what an earlier pick wrote, i.e. not edited by the user since.
  prefilledByPick(field) {
    return this.prefilled.has(field) && this.prefilled.get(field) === field.value
  }

  // `input` (bubbling) is what the drawer's live preview listens for, so the summary
  // reprices with the prefilled values.
  write(field, value) {
    this.prefilled.set(field, value)
    if (field.value === value) return

    field.value = value
    field.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
